import { useEffect, useState } from "react";
import { movimentacaoObraService } from "./service/movimentacao-obra.service";
import useLoadingStore from "../../shared/store/loading.store";
import { useNavigate, useParams } from "react-router-dom";
import { BaseLayout } from "../../shared/layouts/base-layout-page";
import { FerramanetasDetalhe } from "../../shared/components/ferramentasDaListagem/ferramentas-detalhe";
import { TabelaDaListagem } from "../../shared/components/tabela-da-listagem/tabelaDalistagem";
import {
  Box,
  Button,
  Chip,
  Icon,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Modal,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { RetornoDaListagemVazia } from "../../shared/components/retornoDaListagem/retornoDaListagem";
import { ConstructionMovementEnum } from "../../shared/utils/enum/enum";
import { formatDate, formatDateBR } from "../../shared/utils";
import { set, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  CancelarMovimentacaoObraFormData,
  cancelarMovimentacaoObraSchema,
} from "./schema/cancelar-movimentacao-obra.schema.ts";
import InputData from "../../shared/components/formulario/input-data/inputData";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { toastError } from "../../shared/services/errors/erros-handler";
import { ENVIRONMENT } from "../../environment/environment";
import { ModalCancelamento } from "../../shared/components/ModalCancelar/modal-cancelar";

interface Movimentacao {
  id: string;
  movementDate: string;
  description: string;
  additionalValue: string;
  status: string;
  cancellationDate: string | null;
}

export function ListarMovimentacaoObra() {
  const { setLoading } = useLoadingStore();
  const [movimentacoes, setMovimentacoes] = useState<Movimentacao[]>([]);
  const [open, setOpen] = useState(false);
  const [openModalDetalhe, setOpenModalDetalhe] = useState(false);
  const [idMovimentacao, setIdMovimentacao] = useState<string>("");
  const [detalheMovimentacao, setDetalheMovimentacao] = useState<any>(null);
  const { id } = useParams<{ id: string }>();
  const baseUrl = ENVIRONMENT.BASE_URL_API;
  const navigate = useNavigate();

  useEffect(() => {
    const fetchMovimentacoes = async () => {
      if (id) {
        try {
          setLoading(true);
          const mov = await movimentacaoObraService.findAll(id);
          setMovimentacoes(mov);
        } catch (error) {
          setMovimentacoes([]);
        } finally {
          setLoading(false);
        }
      }
    };
    fetchMovimentacoes();
  }, [id]);

  const handleCancel = (idMovimentacaoParam: string) => {
    if (idMovimentacaoParam && idMovimentacaoParam.trim() !== "") {
      setIdMovimentacao(idMovimentacaoParam);
      setOpen(true);
    }
  };

  const handleDetalhar = (idMovimentacaoParam: string) => {
    if (idMovimentacaoParam && idMovimentacaoParam.trim() !== "") {
      setIdMovimentacao(idMovimentacaoParam);
      setOpenModalDetalhe(true);
    }
  };

  useEffect(() => {
    const fetchDetalheMovimentacao = async () => {
      if (idMovimentacao) {
        try {
          const mov = await movimentacaoObraService.detalhar(idMovimentacao);
          setDetalheMovimentacao(mov);
        } catch (error) {
          setDetalheMovimentacao(null);
        }
      }
    };
    fetchDetalheMovimentacao();
  }, [idMovimentacao]);

  const methods = useForm<CancelarMovimentacaoObraFormData>({
    resolver: yupResolver(cancelarMovimentacaoObraSchema),
  });

  const {
    handleSubmit,
    setValue,
    control,
    setError,
    formState: { errors },
  } = methods;

  const { isPending, mutate } = useMutation({
    mutationFn: async (data: CancelarMovimentacaoObraFormData) => {
      setLoading(true, "Estamos quase lá! Só mais um momento...");

      await movimentacaoObraService.cancelar(idMovimentacao, data);
    },
    onSuccess: () => {
      toast.success("Movimentação cancelada com sucesso");
      setOpen(false);
    },
    onError: (err: any) => {
      toastError(err);
    },
    onSettled: () => {
      setLoading(false);
    },
  });

  return (
    <BaseLayout title="Movimentações da Obra">
      <FerramanetasDetalhe
        mostraBotaoApagar={false}
        mostraBotaoNovo={false}
        mostraBotaoSalvarEVoltar={true}
        mostrarBotaoAdicional={true}
        textoBotaoAdicional="Serviços adicionais"
        iconBotaoAdicional="trending_up"
        aoClicarBotaoAdicional={() =>
          navigate(`/obra/servicos-adicionais/${id}`)
        }
        mostraBotaoVoltar
        aoClicarBotaoVoltar={() => navigate(`/obra/detalhe/${id}`)}
      />

      <ModalCancelamento
        open={open}
        onClose={() => setOpen(false)}
        onSubmit={(formData) => mutate(formData)}
        methods={methods}
        isPending={isPending}
      />

      <Modal
        open={openModalDetalhe}
        onClose={() => setOpenModalDetalhe(false)}
        sx={{
          "& .MuiBackdrop-root": {
            backgroundColor: "rgba(0, 0, 0, 0.8)",
          },
        }}
      >
        <Box
          bgcolor="background.paper" // Use a cor do tema
          color="text.primary"
          p={4}
          borderRadius={2}
          boxShadow={24}
          minWidth={350}
          maxWidth={400}
          overflow={"auto"}
          mx="auto"
          mt="10vh"
          display="flex"
          flexDirection="column"
          gap={2}
        >
          <Typography variant="h6">Movimentação detalhada</Typography>
          {detalheMovimentacao && (
            <List sx={{ height: "480px", overflowY: "auto" }}>
              <ListItem>
                <ListItemText
                  primary="Descrição"
                  secondary={detalheMovimentacao?.description || "-"}
                />
              </ListItem>

              <ListItem>
                <ListItemText
                  primary="Data da Movimentação"
                  secondary={
                    formatDateBR(
                      formatDate(detalheMovimentacao?.movementDate)
                    ) || "-"
                  }
                />
              </ListItem>

              <ListItem>
                <ListItemText
                  primary="Responsável por gerar a movimentação"
                  secondary={detalheMovimentacao?.userCreated?.fullName || "-"}
                />
              </ListItem>
              {detalheMovimentacao?.status ===
                ConstructionMovementEnum.CANCELED && (
                <>
                  <ListItem>
                    <ListItemText
                      primary="Data de Cancelamento"
                      secondary={
                        formatDateBR(
                          formatDate(detalheMovimentacao?.cancellationDate)
                        ) || "-"
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Responsável por cancelar a movimentação"
                      secondary={
                        detalheMovimentacao?.userCanceler?.fullName || "-"
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Motivo do Cancelamento"
                      secondary={detalheMovimentacao?.cancellationReason || "-"}
                    />
                  </ListItem>
                </>
              )}

              <ListItem>
                <ListItemText
                  primary="Contrato"
                  secondary={
                    <Button
                      href={`${baseUrl}/${
                        detalheMovimentacao?.contract || "-"
                      }`}
                      target="_blank"
                    >
                      Visualizar
                    </Button>
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Valor Adicional"
                  secondary={
                    detalheMovimentacao?.additionalValue.toLocaleString(
                      "pt-BR",
                      {
                        style: "currency",
                        currency: "BRL",
                        minimumFractionDigits: 2,
                      }
                    ) || "-"
                  }
                />
              </ListItem>
            </List>
          )}
          <Button
            variant="outlined"
            onClick={() => setOpenModalDetalhe(false)}
            color="inherit"
          >
            Fechar
          </Button>
        </Box>
      </Modal>

      <TabelaDaListagem
        ariaLabel="tabela de movimentações"
        headTable={[
          "Data da Movimentação",
          "Descrição",
          "Valor Adicional",
          "Status",
          "Ações",
        ]}
        columnWidths={[100, 100, 100, 100, 100]}
        tableBody={
          movimentacoes?.length > 0 ? (
            movimentacoes.map((mov: Movimentacao) => (
              <TableRow hover key={mov.id} sx={{ height: 40 }}>
                <TableCell sx={{ minWidth: 100 }}>
                  {formatDateBR(formatDate(mov.movementDate))}
                </TableCell>
                <TableCell sx={{ minWidth: 100 }}>
                  {mov.description || "--"}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ minWidth: 100, textAlign: "left" }}
                >
                  {Number(mov.additionalValue).toLocaleString("pt-BR", {
                    style: "currency",
                    currency: "BRL",
                    minimumFractionDigits: 2,
                  })}
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ minWidth: 100, textAlign: "left" }}
                >
                  {mov.status === ConstructionMovementEnum.GENERATED ? (
                    <Chip
                      label="Gerado"
                      color="success"
                      size="small"
                      variant="outlined"
                    />
                  ) : (
                    <Chip
                      label="Cancelado"
                      color="error"
                      size="small"
                      variant="outlined"
                    />
                  )}
                </TableCell>
                <TableCell align="left" sx={{ minWidth: 100 }}>
                  <IconButton
                    size="small"
                    onClick={() => handleDetalhar(mov.id)}
                  >
                    <Icon>visibility</Icon>
                  </IconButton>
                  {mov.status === ConstructionMovementEnum.GENERATED && (
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleCancel(mov.id)}
                    >
                      <Icon>cancel</Icon>
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={5}>
                <RetornoDaListagemVazia />
              </TableCell>
            </TableRow>
          )
        }
      />
    </BaseLayout>
  );
}
